//src/services/tenants.ts
import { api } from "@/lib/apiClient";

export type ListTenantsParams = {
  q?: string;
  limit?: number;
  offset?: number;
};

export type TenantListItem = {
  id: string;
  person_id: string;
  name: string;
  legal_name?: string | null;
  document_number?: string | null;
  slug: string;
  domain?: string | null;
  state_registration?: string | null;
  municipal_registration?: string | null;
  primary_color: string;
  secondary_color: string;
  timezone: string;
  locale: string;
  is_active: boolean;
  document_storage_limit_mb: number;
  active_employees_count?: number;
  units_count?: number;
  onboarding_status?: string | null;
  onboarding_admin_user_id?: string | null;
  onboarding_admin_user_email?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
};

export type TenantContactOut = {
  id: string;
  kind: "email" | "phone" | "whatsapp" | "other";
  value: string;
  is_primary: boolean;
  created_at?: string;
};

export type TenantAddressOut = {
  id: string;
  label?: string | null;
  zip?: string | null;
  street?: string | null;
  number?: string | null;
  complement?: string | null;
  district?: string | null;
  city?: string | null;
  state?: string | null;
  country?: string | null;
  is_primary: boolean;
  created_at?: string;
};

export type TenantDetailOut = {
  tenant: {
    id: string;
    person_id: string;
    slug: string;
    domain?: string | null;
    state_registration?: string | null;
    municipal_registration?: string | null;
    primary_color: string;
    secondary_color: string;
    timezone: string;
    locale: string;
    is_active: boolean;
    document_storage_limit_mb: number;
    created_at?: string | null;
    updated_at?: string | null;
  };
  person: {
    id: string;
    person_type: "PF" | "PJ";
    name: string;
    legal_name?: string | null;
    document_number?: string | null;
    is_active: boolean;
    created_at?: string | null;
    updated_at?: string | null;
    photo?: {
      base64: string;
      mime_type: string;
    } | null;
    photo_file_name?: string | null;
    photo_size_bytes?: number | null;
  };
  contacts: TenantContactOut[];
  addresses: TenantAddressOut[];
};

export type TenantUpsertIn = {
  person: {
    person_type: "PJ";
    name: string;
    legal_name?: string | null;
    document_number?: string | null;
    photo?: {
      photo_base64?: string | null;
      photo_mime_type?: string | null;
      photo_file_name?: string | null;
    } | null;
    contacts: Array<{
      kind: "email" | "phone" | "whatsapp" | "other";
      value: string;
      is_primary: boolean;
    }>;
    addresses: Array<{
      label?: string | null;
      zip?: string | null;
      street?: string | null;
      number?: string | null;
      complement?: string | null;
      district?: string | null;
      city?: string | null;
      state?: string | null;
      country?: string | null;
      is_primary: boolean;
    }>;
  };
  slug: string;
  domain?: string | null;
  state_registration?: string | null;
  municipal_registration?: string | null;
  primary_color?: string;
  secondary_color?: string;
  timezone?: string;
  locale?: string;
  is_active?: boolean;
  document_storage_limit_mb?: number;
};


export type TenantSmtpSettings = {
  tenant_id: string;
  host: string;
  port: number;
  username: string;
  security_mode: "ssl_tls" | "starttls" | "none";
  sender_name: string;
  sender_email: string;
  reply_to?: string | null;
  connection_timeout_seconds: number;
  batch_size: number;
  rate_limit_per_minute: number;
  is_active: boolean;
  has_password: boolean;
  updated_at: string;
};

export type TenantSmtpSettingsInput = {
  host: string;
  port: number;
  username: string;
  password?: string;
  security_mode: "ssl_tls" | "starttls" | "none";
  sender_name: string;
  sender_email: string;
  reply_to?: string | null;
  connection_timeout_seconds: number;
  batch_size: number;
  rate_limit_per_minute: number;
  is_active: boolean;
};

export type TenantSmtpSettingsTestInput = {
  test_email: string;
};

export type TenantSmtpSettingsTestOut = {
  success: boolean;
  message: string;
};

export async function listTenants(params: ListTenantsParams = {}) {
  const { q = "", limit = 50, offset = 0 } = params;

  return api.get<TenantListItem[]>("/tenants", {
    params: { q, limit, offset },
  });
}

export async function getTenantDetail(tenantId: string) {
  return api.get<TenantDetailOut>(`/tenants/${tenantId}`);
}

export async function createTenant(payload: TenantUpsertIn) {
  return api.post<{ id: string }>("/tenants", payload);
}

export async function updateTenant(tenantId: string, payload: TenantUpsertIn) {
  return api.put<{ id: string }>(`/tenants/${tenantId}`, payload);
}

export async function setTenantActive(tenantId: string, is_active: boolean) {
  return api.patch<{ id: string }>(`/tenants/${tenantId}`, { is_active });
}


export async function getTenantSmtpSettings(tenantId: string) {
  return api.get<TenantSmtpSettings | null>(`/tenants/${tenantId}/smtp-settings`);
}

export async function upsertTenantSmtpSettings(tenantId: string, payload: TenantSmtpSettingsInput) {
  return api.put<TenantSmtpSettings>(`/tenants/${tenantId}/smtp-settings`, payload);
}

export async function testTenantSmtpSettings(tenantId: string, payload: TenantSmtpSettingsTestInput) {
  return api.post<TenantSmtpSettingsTestOut>(`/tenants/${tenantId}/smtp-settings/test`, payload);
}

export type TenantOnboardingStatus = {
  tenant_id: string;
  status: "pending" | "in_progress" | "configured" | string;
  is_configured?: boolean;
  is_read_only?: boolean;
  can_edit_onboarding?: boolean;
  can_run_templates?: boolean;
  can_import_structure?: boolean;
  can_send_welcome_email?: boolean;
  unit_id?: string | null;
  unit_name?: string | null;
  department_id?: string | null;
  department_name?: string | null;
  job_position_id?: string | null;
  job_position_name?: string | null;
  group_id?: string | null;
  group_name?: string | null;
  admin_user_id?: string | null;
  admin_user_email?: string | null;
  admin_user_name?: string | null;
  admin_user_phone?: string | null;
  welcome_email_sent_at?: string | null;
  template_summary?: Record<string, TenantOnboardingSummarySection>;
  organization_import_summary?: Record<string, TenantOnboardingSummarySection>;
  onboarding_completed_at?: string | null;
  onboarding_completed_by?: string | null;
  template_selection?: Record<string, string[]>;
  organization_import_filename?: string | null;
};


export type TenantOnboardingTemplateItem = {
  id: string;
  label: string;
  description?: string | null;
  meta?: string | null;
};

export type TenantOnboardingTemplates = {
  source_tenant_id: string;
  dimensions: TenantOnboardingTemplateItem[];
  risks: TenantOnboardingTemplateItem[];
  action_plan_models: TenantOnboardingTemplateItem[];
  groups: TenantOnboardingTemplateItem[];
  dimension_risk_rules: TenantOnboardingTemplateItem[];
  risk_action_plan_models: TenantOnboardingTemplateItem[];
  questionnaires: TenantOnboardingTemplateItem[];
};

export type TenantOnboardingSummarySection = {
  created: number;
  skipped_existing: number;
  skipped_dependency: number;
  errors: string[];
};

export type TenantOnboardingOrganizationImportFile = {
  enabled: boolean;
  filename?: string | null;
  content_base64?: string | null;
};

export type TenantOnboardingOrganizationImportValidation = {
  valid: boolean;
  total_rows: number;
  errors: string[];
  preview_rows: Array<Record<string, string>>;
};

export type TenantOnboardingInput = {
  structure: {
    unit_name: string;
    department_name: string;
    job_position_name: string;
  };
  admin_group: {
    name: string;
    permission_codes: string[];
  };
  admin_user: {
    name: string;
    email: string;
    phone?: string | null;
    is_active: boolean;
  };
  template_selection?: {
    migrate_templates: boolean;
    dimension_ids: string[];
    risk_ids: string[];
    action_plan_model_ids: string[];
    group_ids: string[];
    dimension_risk_rule_ids: string[];
    risk_action_plan_model_ids: string[];
    questionnaire_ids: string[];
  };
  organization_import?: TenantOnboardingOrganizationImportFile;
};

export type TenantOnboardingResult = {
  tenant_id: string;
  tenant_name: string;
  status: string;
  unit_id: string;
  department_id: string;
  job_position_id: string;
  group_id: string;
  admin_user_id: string;
  admin_user_email: string;
  admin_user_name: string;
  temporary_password: string;
  email_sent: boolean;
  template_summary?: Record<string, TenantOnboardingSummarySection>;
  organization_import_summary?: Record<string, TenantOnboardingSummarySection>;
};

export type TenantOnboardingWelcomeEmailResult = {
  success: boolean;
  message: string;
  admin_user_id: string;
  admin_user_email: string;
  email_sent: boolean;
};


export async function getTenantOnboardingTemplates(tenantId: string) {
  return api.get<TenantOnboardingTemplates>(`/admin/tenants/${tenantId}/onboarding/templates`);
}

export async function validateTenantOnboardingOrganizationImport(
  tenantId: string,
  payload: { filename: string; content_base64: string },
) {
  return api.post<TenantOnboardingOrganizationImportValidation>(
    `/admin/tenants/${tenantId}/onboarding/organization-import/validate`,
    payload,
  );
}

export async function downloadTenantOnboardingOrganizationImportTemplate() {
  return api.download(`/admin/tenants/onboarding/organization-import-template`);
}

export async function getTenantOnboardingStatus(tenantId: string) {
  return api.get<TenantOnboardingStatus>(`/admin/tenants/${tenantId}/onboarding`);
}

export async function createTenantOnboarding(tenantId: string, payload: TenantOnboardingInput) {
  return api.post<TenantOnboardingResult>(`/admin/tenants/${tenantId}/onboarding`, payload);
}

export async function sendTenantOnboardingWelcomeEmail(tenantId: string, userId: string) {
  return api.post<TenantOnboardingWelcomeEmailResult>(
    `/admin/tenants/${tenantId}/onboarding/send-welcome-email`,
    { user_id: userId },
  );
}
